import { useMemo, useState } from "react";
import {
  AsyncSearchableSelect,
  type AsyncSelectOption,
} from "@/components/Form/AsyncSearchableSelect";
import { SYSTEM_ACTOR } from "@/api/changeAudit.service";
import { userManagement } from "@/api/userManagement.service";
import styles from "./AuditLog.module.css";

const SYSTEM_OPTION: AsyncSelectOption = {
  value: SYSTEM_ACTOR,
  label: "System",
  description: "Automated changes",
};

type ActorPickerProps = {
  value: string;
  onChange: (actor: string | null) => void;
};

async function searchActors(query: string): Promise<AsyncSelectOption[]> {
  const term = query.trim();
  const { users } = await userManagement.getUsers({
    search: term || undefined,
    context: "staff",
    limit: 10,
  });
  const people = users.map(user => {
    const label = user.name || user.email || user.id;
    return {
      // Audit entries store the email for every user-made change; the id is a fallback.
      value: user.email || user.id,
      label,
      description: user.email && user.email !== label ? user.email : undefined,
    };
  });
  const showSystem = !term || "system".includes(term.toLowerCase());
  return showSystem ? [SYSTEM_OPTION, ...people] : people;
}

/** Shows the chosen person as name and email; after a reload only the actor itself is known. */
function displayOption(actor: string, picked: AsyncSelectOption | null): AsyncSelectOption | null {
  if (!actor) return null;
  if (actor === SYSTEM_ACTOR) return SYSTEM_OPTION;
  if (picked?.value === actor && picked.description) {
    return { value: actor, label: `${picked.label} (${picked.description})` };
  }
  return { value: actor, label: actor };
}

export function ActorPicker({ value, onChange }: ActorPickerProps) {
  const [picked, setPicked] = useState<AsyncSelectOption | null>(null);
  const initialOption = useMemo(() => displayOption(value, picked), [value, picked]);

  return (
    <div className={styles.actorPicker} title={initialOption?.label}>
      <AsyncSearchableSelect
        name="audit-actor"
        label="Who"
        value={value || null}
        placeholder="Anyone"
        onChange={next => onChange(next === null ? null : String(next))}
        onOptionChange={setPicked}
        onSearch={searchActors}
        minSearchLength={0}
        initialOption={initialOption}
      />
    </div>
  );
}
